import { z } from 'zod';

/**
 * Admin "Costs" dashboard: LLM spend aggregated live from the `llm_calls` ledger
 * (every tutor / scoring / authoring / roadmap / cv / jd call records its
 * tokens + cost). Nothing here is hardcoded — it's grouped straight from the DB.
 */

const moneyStats = z.object({
  costUsd: z.number(),
  calls: z.number().int(),
  inputTokens: z.number().int(),
  outputTokens: z.number().int(),
});

export const adminCostByPurposeSchema = moneyStats.extend({ purpose: z.string() });

/**
 * PRE-131: the roll-up above `purpose` — who the spend was for.
 *
 * `purpose` says what ran; this says whether it ran for a learner or for us.
 * The two biggest numbers in the ledger sit next to each other in a flat list
 * and are not comparable quantities: one grows with users, the other with how
 * often we point our own instruments at the repository.
 */
export const adminCostByGroupSchema = moneyStats.extend({
  group: z.string(),
  label: z.string(),
  learnerFacing: z.boolean(),
  /** The purposes rolled up here, biggest first — so a group can be opened up. */
  purposes: z.array(z.string()),
});
export const adminCostByModelSchema = z.object({
  provider: z.string(),
  model: z.string(),
  costUsd: z.number(),
  calls: z.number().int(),
});
export const adminCostByStatusSchema = z.object({
  status: z.string(),
  calls: z.number().int(),
  costUsd: z.number(),
});
export const adminCostByDaySchema = z.object({
  day: z.string(),
  costUsd: z.number(),
  calls: z.number().int(),
});

export const adminCostsResponseSchema = z.object({
  /** All-time totals across every LLM call. */
  totals: moneyStats,
  /** Rolling window (default 30 days). */
  window: z.object({ days: z.number().int(), costUsd: z.number(), calls: z.number().int() }),
  /** Month-to-date total + the authoring monthly cap (from env), for budget context. */
  monthToDateUsd: z.number(),
  monthlyCapUsd: z.number().nullable(),
  byPurpose: z.array(adminCostByPurposeSchema),
  byGroup: z.array(adminCostByGroupSchema),
  /** The headline split: what the product cost vs what our own tooling cost. */
  split: z.object({
    learnerFacingUsd: z.number(),
    internalUsd: z.number(),
  }),
  byModel: z.array(adminCostByModelSchema),
  byStatus: z.array(adminCostByStatusSchema),
  /** Per-day spend for the window, oldest → newest. */
  byDay: z.array(adminCostByDaySchema),
  /**
   * PRE-143: newest ledger rows so the panel can deep-link into Datadog LLM
   * Observability. `llmTraceUrl` is null for rows ingested before span ids
   * were stored.
   */
  recentCalls: z.array(
    z.object({
      id: z.string(),
      purpose: z.string(),
      status: z.string(),
      provider: z.string(),
      model: z.string(),
      costUsd: z.number(),
      latencyMs: z.number().int(),
      createdAt: z.string(),
      userId: z.string().nullable(),
      llmTraceUrl: z.string().url().nullable(),
    }),
  ),
});
export type AdminCostsResponse = z.infer<typeof adminCostsResponseSchema>;
